import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { ButtonGroup } from '@atlaskit/button';
import { Flex, xcss, media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { BoardConnectToolbar } from '@atlassian/jira-software-connect-toolbar/src/ui/board-connect-toolbar/index.tsx';
import { ImportWorkButton } from '@atlassian/jira-software-onboarding-invites/src/ui/import-work-button/async.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsEmbedView } from '../../../common/hooks/use-is-embed-view';
import type { PanelType } from '../../../model/panels/types';
import { useBacklogDispatch, useBacklogSelector } from '../../../state';
import { togglePanel } from '../../../state/actions/panels';
import { getIsCompanyManaged, getProjectKey, getRapidViewId } from '../../../state/selectors/app';
import { isBacklogWithSprints } from '../../../state/selectors/sprints';
import { useIsInsightsPanelOpen } from '../../../state/state-hooks/use-is-insights-panel-open';
import Operations from '../extension';
import Menu from '../menu';
import { FullscreenButton } from './fullscreen-button';
import { InsightsButton } from './insights-button';
import { ShareButton } from './share-button';
import BacklogViewSettings from './view-settings';

export type Props = {
	isMenuVisible: boolean;
	boardType?: string;
};

const ActionsBarNew = ({ isMenuVisible }: Props) => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const isEmbedView = fg('enable_backlog_smartlink_route') ? useIsEmbedView() : false;
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const rapidViewId = useBacklogSelector(getRapidViewId);
	const projectKey = useBacklogSelector(getProjectKey);
	const appEditions = useAppEditions();
	const isInsightsOpen = useIsInsightsPanelOpen();
	const backlogWithSprints = useBacklogSelector(isBacklogWithSprints);
	const dispatch = useBacklogDispatch();

	const handleOnToggle = useCallback(
		(panelType: PanelType) => {
			dispatch(togglePanel(panelType));
		},
		[dispatch],
	);

	return (
		<Flex justifyContent="end" wrap="wrap">
			<Flex justifyContent="end" xcss={extensionsStyles}>
				<Operations />
				{isCompanyManaged && (
					<BoardConnectToolbar boardId={rapidViewId} projectKey={projectKey} mode="plan" />
				)}
			</Flex>
			<ButtonGroup>
				{!isEmbedView && (
					<>
						<ImportWorkButton />
						<FullscreenButton boardId={Number(rapidViewId)} />
					</>
				)}
				{backlogWithSprints && (
					<InsightsButton
						onToggle={handleOnToggle}
						isInsightsPanelOpen={isInsightsOpen}
						boardId={Number(rapidViewId)}
						edition={appEditions.software}
					/>
				)}
				<BacklogViewSettings />
				{isMenuVisible && !isEmbedView && <Menu />}
			</ButtonGroup>
		</Flex>
	);
};

const ActionsBarOld = ({ isMenuVisible, boardType }: Props) => {
	const isEmbedView = useIsEmbedView();
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const rapidViewId = useBacklogSelector(getRapidViewId);
	const projectKey = useBacklogSelector(getProjectKey);

	const shareContentType = `${boardType}_backlog`;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onShareButtonClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(analyticsEvent, 'shareContentType', {
			contentType: shareContentType,
		});
	};
	const iconsComponent = (
		<>
			<Operations />
			<ShareButton shareContentType={shareContentType} onTriggerButtonClick={onShareButtonClick} />
			<FullscreenButton boardId={Number(rapidViewId)} />
		</>
	);

	return isEmbedView ? null : (
		<ActionsBarWrapper>
			<Flex>{iconsComponent}</Flex>
			{isCompanyManaged && (
				<BoardConnectToolbar boardId={rapidViewId} projectKey={projectKey} mode="plan" />
			)}
			{isMenuVisible ? (
				<MenuWrapper>
					<Menu />
				</MenuWrapper>
			) : null}
		</ActionsBarWrapper>
	);
};

export const ActionsBar = componentWithCondition(
	() => getWillShowNav4(),
	ActionsBarNew,
	ActionsBarOld,
);

const extensionsStyles = xcss({
	paddingBottom: 'space.100',
	gap: 'space.100',
	width: '100%',
	flex: '0 1 auto',
	flexWrap: 'wrap',
	[media.above.md]: {
		flex: '0 0 auto',
		flexWrap: 'wrap',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsBarWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuWrapper = styled.div({
	width: 'auto',
});
